import React from 'react';
import { 
  MessageCircle, Mail, ExternalLink, Shield, Terminal, Lock, Sparkles, 
  Download, CheckCircle2, ArrowRight, Laptop, HardDrive, Cpu
} from 'lucide-react';
import contentData from '../data/website_content.json';
import { useRouter } from '../context/RouterContext';
import { CURRENT_VERSION, DOWNLOAD_FILENAME, DOWNLOAD_SIZE, RELEASE_DATE, GOOGLE_DRIVE_DOWNLOAD_URL } from '../config/version';

export const Footer: React.FC = () => {
  const { brand, contact } = contentData;
  const { navigate } = useRouter();

  return (
    <footer className="relative bg-gradient-to-b from-obsidian via-plum-black to-wine border-t border-white/[0.08] pt-16 pb-12 overflow-hidden font-sans">
      
      {/* Subtle Plum Halftone Dot Matrix Texture */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(165, 106, 232, 0.08) 1.2px, transparent 1.2px)',
          backgroundSize: '16px 16px',
        }}
      />

      {/* Ambient Environmental Lighting Accents (Rose & Burgundy) */}
      <div className="absolute top-0 left-1/4 w-96 h-40 bg-rose/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-96 h-40 bg-burgundy/15 rounded-full blur-3xl pointer-events-none" />

      {/* ── Top Production Banner / Download Software CTA ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-16 relative z-10">
        <div className="p-6 sm:p-8 rounded-3xl bg-charcoal/90 border border-rose/25 shadow-elevated backdrop-blur-xl flex flex-col lg:flex-row items-center justify-between gap-6">
          
          <div className="space-y-2 text-center lg:text-left">
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2.5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose/15 border border-rose/30 text-rose text-xs font-mono font-bold">
                <span className="w-2 h-2 rounded-full bg-jade animate-pulse" />
                COMPILED PRODUCTION RUNTIME
              </span>
              <span className="text-xs font-mono text-muted">
                v{CURRENT_VERSION} · Win64 Native Client
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-black text-ivory tracking-tight">
              Ready to Deploy on Your Windows Workstation?
            </h3>
            <p className="text-xs sm:text-sm text-muted max-w-xl">
              100% local execution on your PC with hardware-backed Windows encryption. No cloud dependencies.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto shrink-0">
            {/* Primary Download Software Button (Rose → Vermilion) */}
            <a
              href={GOOGLE_DRIVE_DOWNLOAD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download-action w-full sm:w-auto px-8 py-4 rounded-xl text-white font-bold text-sm sm:text-base flex items-center justify-center gap-2.5 cursor-pointer font-sans"
            >
              <Download className="w-5 h-5 stroke-[2.5]" />
              <span>DOWNLOAD v{CURRENT_VERSION}</span>
            </a>

            {/* Chat with Mueed Lashari on WhatsApp */}
            <a
              href={contact.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-5 py-4 rounded-2xl bg-charcoal hover:bg-wine/40 border border-white/10 text-soft-white hover:text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md"
            >
              <MessageCircle className="w-4 h-4 text-rose" />
              <span>Chat with Mueed Lashari: {contact.whatsappDisplay || "+92 334 788 1924"}</span>
            </a>

            {/* Chat with Aswad Lashari on WhatsApp */}
            <a
              href={contact.secondaryWhatsappUrl || contact.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-5 py-4 rounded-2xl bg-charcoal hover:bg-wine/40 border border-white/10 text-soft-white hover:text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md"
            >
              <MessageCircle className="w-4 h-4 text-jade" />
              <span>Chat with Aswad Lashari: {contact.secondaryWhatsappDisplay || "+92 371 043 4552"}</span>
            </a>
          </div>

        </div>
      </div>

      {/* ── Main Multi-Column Links Section ── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-14">
          
          {/* Column 1: Brand & Architecture */}
          <div className="space-y-4 lg:col-span-1">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-action p-[1.5px] shadow-sm">
                <div className="w-full h-full bg-charcoal rounded-[10px] flex items-center justify-center">
                  <Terminal className="w-5 h-5 text-rose" />
                </div>
              </div>
              <div>
                <span className="font-black text-ivory text-base tracking-tight block">
                  LASHARY
                </span>
                <span className="text-xs text-rose font-mono font-semibold">
                  FB Market Suite v{CURRENT_VERSION}
                </span>
              </div>
            </div>
            <p className="text-xs text-muted leading-relaxed">
              Commercial Windows desktop software for Facebook Marketplace batch automation, deterministic media allocation, and safe review-first publishing.
            </p>
            <div className="pt-1">
              <span className="text-[11px] font-mono px-2.5 py-1 rounded-lg bg-charcoal/90 border border-white/[0.08] text-soft-white flex items-center gap-1.5 w-fit">
                <Shield className="w-3.5 h-3.5 text-jade" />
                Windows 10/11 64-Bit Strictly
              </span>
            </div>
          </div>

          {/* Column 2: Product & Workstations */}
          <div className="space-y-3.5">
            <h4 className="text-xs font-mono uppercase tracking-wider text-rose font-bold">
              Product & Workstations
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button onClick={() => navigate('/features')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Features & Capabilities Matrix
                </button>
              </li>
              <li>
                <a 
                  href={GOOGLE_DRIVE_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted hover:text-soft-white transition-colors flex items-center gap-1.5 cursor-pointer"
                >
                  <span>Download Software (v{CURRENT_VERSION})</span>
                  <span className="text-[10px] font-mono text-jade font-bold bg-jade/10 px-1 rounded">EXE</span>
                </a>
              </li>
              <li>
                <button onClick={() => navigate('/changelog')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Release History & Changelog
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/pricing')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Pricing (PKR 1,600 / $15 mo)
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/features/proxy')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Proxy Center & Session Sandbox
                </button>
              </li>
            </ul>
          </div>

          {/* Column 3: Tools & Extensions */}
          <div className="space-y-3.5">
            <h4 className="text-xs font-mono uppercase tracking-wider text-apricot font-bold">
              Tools & Directory
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button onClick={() => navigate('/tools')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Free Marketplace Tools Hub
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/tools/facebook-marketplace-auto-poster')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  FB Marketplace Auto Poster
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/tools/facebook-marketplace-bulk-posting')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Bulk Posting Engine
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/tools/facebook-marketplace-relisting-tool')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Relisting & Refresh Tool
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/comparisons')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Software vs Browser Extensions
                </button>
              </li>
            </ul>
          </div>

          {/* Column 4: Resources & Guides */}
          <div className="space-y-3.5">
            <h4 className="text-xs font-mono uppercase tracking-wider text-orchid font-bold">
              Resources & Intel
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button onClick={() => navigate('/blog')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Operator Guides & Blog
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/tutorials')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Setup & Walkthrough Tutorials
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/faq')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Frequently Asked Questions
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/support')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Customer Support Hub
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/reseller')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Agency & Reseller Program
                </button>
              </li>
            </ul>
          </div>

          {/* Column 5: Company & Direct Support */}
          <div className="space-y-3.5">
            <h4 className="text-xs font-mono uppercase tracking-wider text-gold font-bold">
              Company & Contact
            </h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <button onClick={() => navigate('/about')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  About Lashary & Founders
                </button>
              </li>
              <li>
                <a 
                  href={contact.whatsappUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-jade hover:text-jade-light transition-colors flex items-center gap-1.5"
                >
                  <MessageCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>WhatsApp: {contact.whatsappDisplay}</span>
                </a>
              </li>
              <li>
                <a 
                  href={`mailto:${contact.email}`} 
                  className="text-muted hover:text-soft-white transition-colors flex items-center gap-1.5"
                >
                  <Mail className="w-3.5 h-3.5 shrink-0" />
                  <span>{contact.email}</span>
                </a>
              </li>
              <li>
                <button onClick={() => navigate('/privacy')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Privacy Policy & Data Security
                </button>
              </li>
              <li>
                <button onClick={() => navigate('/terms')} className="text-muted hover:text-soft-white transition-colors cursor-pointer text-left">
                  Terms of Service & Licensing
                </button>
              </li>
            </ul>
          </div>

        </div>

        {/* ── Bottom Operational Status & Copyright Bar ── */}
        <div className="pt-8 border-t border-white/[0.08] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted font-mono">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-jade animate-pulse" />
            <span className="text-soft-white">All Systems Operational · v{CURRENT_VERSION} Release Build ({RELEASE_DATE})</span>
          </div>

          <div className="flex items-center gap-6">
            <span>© {new Date().getFullYear()} Lashary FB Market Suite. All rights reserved.</span>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
